import styles from './LoadingScreen.module.scss';

import { animate, motion, useMotionValue, useTransform } from 'framer-motion';

import Pollo from './images/logo.svg?react';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

type LoadingScreenProps = {
  setIsInitAnimationEnded: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
};

export const LoadingScreen = ({
  setIsInitAnimationEnded,
  isLoading,
}: LoadingScreenProps) => {
  const count = useMotionValue(1);
  const width = useTransform(count, (value) => `${Math.round(value)}%`);
  const left = useTransform(
    count,
    (value) => `calc(${Math.round(value)}% - 7px)`,
  );

  useEffect(() => {
    const firstStage = animate(count, 90, { duration: 1, delay: 1 });

    firstStage.then(() => {
      if (isLoading) {
        const checkLoading = setInterval(() => {
          if (!isLoading) {
            clearInterval(checkLoading);
            completeAnimation();
          }
        }, 100);
      } else {
        completeAnimation();
      }
    });
  }, [count, isLoading]);

  const completeAnimation = () => {
    const finalStage = animate(count, 101, { duration: 0.3 });

    finalStage.then(() => {
      setTimeout(() => {
        [styles.logo, styles.loadingWrapper, styles.loading].forEach(
          (className) => {
            animate(
              `.${className}`,
              { opacity: 0, scale: 0 },
              { duration: 0.25, ease: 'easeOut' },
            );
          },
        );
        setTimeout(setIsInitAnimationEnded, 400, true);
      }, 300);
    });
  };

  return (
    <motion.div className={styles.screen}>
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        className={styles.logo}
        transition={{
          delay: 0.5,
          duration: 0.4,
          scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
        }}
      >
        <Pollo />
      </motion.div>
      <motion.div
        className={styles.loadingWrapper}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className={styles.loading}
          style={{
            width,
          }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: 1.5,
          }}
          className={styles.loadingBlur}
          style={{
            left,
          }}
        />
      </motion.div>
    </motion.div>
  );
};
